import type { MouseEvent, ReactNode } from "react";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  ExternalLink,
  Loader2,
  PanelRightOpen,
  Play,
  PlugZap,
  Search,
  Settings,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { FOCUS_RING_CLASS } from "@/lib/a11y";
import { cn } from "@/lib/utils";

import { safeHttpUrl } from "./safeHttpUrl";

/** Status de uma linha da Execução, nas cores do Claude Design. */
export type ExecutionRowStatus =
  | "idle"
  | "running"
  | "done"
  | "locked"
  | "failed"
  | "waiting"
  | "interrupted";

const STATUS_LABEL: Record<ExecutionRowStatus, string> = {
  idle: "Not executed",
  running: "Running",
  done: "Executed",
  locked: "Locked",
  failed: "Failed",
  waiting: "Waiting for you",
  interrupted: "Interrupted",
};

/** Cor do texto e do ponto do status. */
const STATUS_TEXT: Record<ExecutionRowStatus, string> = {
  idle: "text-[#596b85]",
  running: "text-[#b07d00]",
  done: "text-[#1e7f43]",
  locked: "text-[#8a97ab]",
  failed: "text-[#d4353c]",
  waiting: "text-[#c2410c]",
  interrupted: "text-[#c2410c]",
};

const STATUS_DOT: Record<ExecutionRowStatus, string> = {
  idle: "bg-[#596b85]",
  running: "bg-[#b07d00]",
  done: "bg-[#1e7f43]",
  locked: "bg-[#8a97ab]",
  failed: "bg-[#d4353c]",
  waiting: "bg-[#f27325]",
  interrupted: "bg-[#f27325]",
};

/** A barra de 3px à esquerda da linha. */
const STATUS_BAR: Record<ExecutionRowStatus, string> = {
  idle: "bg-[rgba(255,200,61,0.55)]",
  running: "bg-[#ffc83d]",
  done: "bg-[#27a155]",
  locked: "bg-[#e8ebf0]",
  failed: "bg-[#d4353c]",
  waiting: "bg-[#f27325]",
  interrupted: "bg-[#f27325]",
};

const CONNECTION_CLASS = {
  ok: "border-[rgba(39,161,85,0.25)] bg-[rgba(39,161,85,0.1)] text-[#1e7f43]",
  warn: "border-[#ffc83d] bg-[#fff8e1] text-[#6b5200]",
  neutral: "border-[#cfd6e2] bg-[#f2f4f7] text-[#596b85]",
} as const;

// O botão do fim do selo (Open / Configure here): texto ao lado do ícone, na cor do selo.
const CONNECTION_ACTION_CLASS = cn(
  "-mr-1 inline-flex h-6 items-center gap-1 rounded-full bg-transparent px-2 text-xs font-semibold text-current underline underline-offset-2 transition-colors hover:bg-black/[0.07]",
  FOCUS_RING_CLASS,
);

const CONNECTION_ICON = {
  ok: CircleCheck,
  warn: AlertTriangle,
  neutral: PlugZap,
} as const;

// Ações sem borda da linha (Open in SAP e detalhes).
const GHOST_ACTION_CLASS = cn(
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-transparent text-[#596b85] transition-colors [&_svg]:h-4 [&_svg]:w-4",
  "hover:bg-[#f1f3f6] hover:text-[#08254c] disabled:cursor-not-allowed disabled:text-[#c3cbd6] disabled:hover:bg-transparent",
  FOCUS_RING_CLASS,
);

const RUN_ACTION_CLASS = cn(
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[rgba(8,37,76,0.15)] bg-white text-[#08254c] transition-colors [&_svg]:h-3.5 [&_svg]:w-3.5",
  "hover:border-[#ffc83d] disabled:cursor-not-allowed disabled:border-[#eef1f5] disabled:bg-[#f7f7f7] disabled:text-[#c3cbd6] disabled:hover:border-[#eef1f5]",
  FOCUS_RING_CLASS,
);

/** A linha também seleciona no clique: as ações não podem chegar até ela. */
const stop = (event: MouseEvent) => event.stopPropagation();

/** O recuo da linha: 20px à esquerda (o antigo pl-5) mais 24px por nível da hierarquia. */
const EXECUTION_ROW_BASE_PADDING = 20;
const EXECUTION_ROW_INDENT = 24;

export interface ExecutionListRow {
  key: string;
  code: string;
  /** O ID da origem (SAP) quando o consultor o editou: vira a tag cinza ao lado da editada. */
  originalCode?: string;
  name: ReactNode;
  /** Só na estrutura organizacional: o tipo da unidade (ex.: Company Code). */
  typeLabel?: string;
  status: ExecutionRowStatus;
  /** Rótulo já traduzido no lugar do padrão do status. */
  statusLabel?: string;
  testId: string;
  /** Só SSCUI: o link para a configuração no SAP; sem `url`, desabilitado com o motivo. */
  launch?: { url: string | null; label: string; disabledReason: string | null };
  /** Só na estrutura organizacional: o nível da unidade na hierarquia (0 é a raiz), que recua a linha. */
  depth?: number;
  /** O ▶; `null` quando a linha não roda (concluída, aguardando a pessoa no terminal). */
  run?: {
    label: string;
    title: string;
    disabled: boolean;
    busy: boolean;
    onRun: () => void;
    testId?: string;
  } | null;
  /** Detalhes da linha: o modal da configuração ou o drawer da unidade (só leitura). */
  details?: {
    label: string;
    onDetails: () => void;
    /** Detalhes indisponíveis (sem extração), com o motivo. */
    disabledReason?: string | null;
  };
}

/** Grupo da lista (a área de aplicação na Execução SSCUI): cabeçalho que abre e fecha, e as linhas dele. */
export interface ExecutionListGroup {
  key: string;
  label: string;
  open: boolean;
  onToggle: () => void;
  rows: ExecutionListRow[];
}

export interface ExecutionConnection {
  tone: keyof typeof CONNECTION_CLASS;
  label: string;
  /** A URL cadastrada no banco; com ela (http/https) o selo ganha o botão que a abre em nova aba. */
  url?: string | null;
  /** Nome acessível do botão ("Open CBC in a new tab"). */
  openLabel?: string;
  /** Só no selo amarelo (não configurado): abre o modal de conexão, o mesmo do gate da Execução. */
  onConfigure?: () => void;
  /** Nome acessível do botão ("Configure the CBC connection"). */
  configureLabel?: string;
}

export interface ExecutionListCardProps {
  overline: string;
  title: string;
  done: number;
  total: number;
  /** O selo da conexão à direita; `null` enquanto carrega. */
  connection?: ExecutionConnection | null;
  search: string;
  onSearchChange: (value: string) => void;
  /** Enter na busca: aplica sem a espera de 3 s. */
  onSearchEnter?: () => void;
  rows: ExecutionListRow[];
  /** Com grupos, as linhas vêm dentro deles (e `rows` é ignorado). */
  groups?: ExecutionListGroup[];
  selectedKey?: string | null;
  onSelect: (key: string) => void;
  /** À direita da busca: o "Run all" e o que o acompanha (Pause, Stop queue). */
  toolbarActions?: ReactNode;
  /** Aviso entre a barra e as linhas (a fila que parou). */
  notice?: ReactNode;
  /** No lugar das linhas: carregando ou erro. */
  body?: ReactNode;
}

/**
 * O cartão da lista da Execução, igual nas três abas (Claude Design, `Execution.dc.html`):
 * cabeçalho com a sobrelinha, o título, concluídos/total e o selo da conexão; a barra com a
 * busca e o "Run all"; as linhas com a barra de status, o código, o nome, o status e as
 * ações. Só desenha: quem o usa decide o que cada linha pode fazer.
 */
export function ExecutionListCard({
  overline,
  title,
  done,
  total,
  connection,
  search,
  onSearchChange,
  onSearchEnter,
  rows,
  groups,
  selectedKey,
  onSelect,
  toolbarActions,
  notice,
  body,
}: ExecutionListCardProps) {
  const { t } = useTranslation();
  const ConnectionIcon = connection ? CONNECTION_ICON[connection.tone] : null;
  // O selo amarelo (não configurado) não tem URL para abrir: tem o "Configure here".
  const connectionUrl =
    connection && connection.tone !== "warn"
      ? safeHttpUrl(connection.url)
      : null;
  return (
    <div className="flex min-w-0 flex-col rounded-lg border border-[#e8ebf0] bg-white">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[#e8ebf0] px-5 pb-4 pt-[18px]">
        <div className="flex flex-col gap-1">
          <span className="font-mono text-[11px] font-medium tracking-[0.12em] text-[#596b85]">
            {overline}
          </span>
          <div className="flex flex-wrap items-center gap-2.5">
            <h2 className="m-0 text-base font-semibold text-[#08254c]">
              {title}
            </h2>
            <span
              data-testid="execution-list-count"
              className="rounded-full bg-[rgba(255,200,61,0.2)] px-2 py-0.5 font-mono text-xs font-semibold text-[#7a5600]"
            >
              {`${done.toLocaleString("en-US")}/${total.toLocaleString("en-US")}`}
            </span>
          </div>
        </div>
        {connection && ConnectionIcon && (
          <span
            data-testid="execution-list-connection"
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold",
              CONNECTION_CLASS[connection.tone],
            )}
          >
            <ConnectionIcon aria-hidden="true" className="h-3.5 w-3.5" />
            {connection.label}
            {connectionUrl && (
              <>
                <span
                  aria-hidden="true"
                  data-testid="execution-connection-separator"
                  className="mx-0.5 h-3.5 w-px bg-current opacity-25"
                />
                <a
                  href={connectionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={connection.openLabel}
                  title={connectionUrl}
                  className={cn(CONNECTION_ACTION_CLASS, "no-underline")}
                >
                  <ExternalLink aria-hidden="true" className="h-3 w-3" />
                  {t("Open", { context: "connection_link" })}
                </a>
              </>
            )}
            {connection.tone === "warn" && connection.onConfigure && (
              <>
                <span
                  aria-hidden="true"
                  data-testid="execution-connection-separator"
                  className="mx-0.5 h-3.5 w-px bg-current opacity-25"
                />
                <button
                  type="button"
                  aria-label={connection.configureLabel}
                  onClick={connection.onConfigure}
                  className={CONNECTION_ACTION_CLASS}
                >
                  <Settings aria-hidden="true" className="h-3 w-3" />
                  {t("Configure here")}
                </button>
              </>
            )}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-[#e8ebf0] px-5 py-3.5">
        <div className="flex h-9 max-w-[380px] flex-[1_1_240px] items-center gap-2 rounded-md border border-[#e8ebf0] bg-white px-3 text-[#596b85] focus-within:border-[#ffc83d]">
          <Search aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
          <input
            type="text"
            aria-label={t("Search code or name…")}
            placeholder={t("Search code or name…")}
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") onSearchEnter?.();
            }}
            className="min-w-0 flex-1 border-none bg-transparent text-[13px] text-[#08254c] outline-none placeholder:text-[#8a97ab]"
          />
        </div>
        {toolbarActions && (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {toolbarActions}
          </div>
        )}
      </div>

      {notice}
      {body ??
        (groups ? (
          groups.length === 0 ? (
            <p className="m-0 px-5 py-8 text-center text-sm text-[#596b85]">
              {t("No items match the search.")}
            </p>
          ) : (
            <ul aria-label={title} className="m-0 flex list-none flex-col p-0">
              {groups.map((group) => (
                <li key={group.key}>
                  <button
                    type="button"
                    aria-expanded={group.open}
                    onClick={group.onToggle}
                    className={cn(
                      "flex w-full items-center gap-2.5 border-b border-[#f0f2f5] bg-[#fafbfc] px-5 py-2.5 text-left hover:bg-[#f1f3f6]",
                      FOCUS_RING_CLASS,
                    )}
                  >
                    {group.open ? (
                      <ChevronDown
                        aria-hidden="true"
                        className="h-3.5 w-3.5 text-[#596b85]"
                      />
                    ) : (
                      <ChevronRight
                        aria-hidden="true"
                        className="h-3.5 w-3.5 text-[#596b85]"
                      />
                    )}
                    <span
                      data-testid="execution-area-header"
                      className="text-sm font-semibold text-[#08254c]"
                    >
                      {group.label}
                    </span>
                    <span className="ml-auto font-mono text-[11px] text-[#596b85]">
                      {group.rows.length.toLocaleString("en-US")}
                    </span>
                  </button>
                  {group.open && (
                    <ul className="m-0 flex list-none flex-col p-0">
                      {group.rows.map((row) => (
                        <ExecutionLine
                          key={row.key}
                          row={row}
                          selected={row.key === selectedKey}
                          onSelect={onSelect}
                        />
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )
        ) : rows.length === 0 ? (
          <p className="m-0 px-5 py-8 text-center text-sm text-[#596b85]">
            {t("No items match the search.")}
          </p>
        ) : (
          <ul aria-label={title} className="m-0 flex list-none flex-col p-0">
            {rows.map((row) => (
              <ExecutionLine
                key={row.key}
                row={row}
                selected={row.key === selectedKey}
                onSelect={onSelect}
              />
            ))}
          </ul>
        ))}
    </div>
  );
}

export function ExecutionLine({
  row,
  selected,
  onSelect,
}: {
  row: ExecutionListRow;
  selected: boolean;
  onSelect: (key: string) => void;
}) {
  const { t } = useTranslation();
  const select = () => onSelect(row.key);
  return (
    <li
      data-testid={row.testId}
      data-row-key={row.key}
      data-depth={row.depth}
      onClick={select}
      style={{
        paddingLeft:
          EXECUTION_ROW_BASE_PADDING + (row.depth ?? 0) * EXECUTION_ROW_INDENT,
      }}
      className={cn(
        "relative flex cursor-pointer items-center gap-3 border-b border-[#f0f2f5] py-3 pr-4 last:border-b-0 [content-visibility:auto] [contain-intrinsic-size:auto_57px]",
        selected ? "bg-[rgba(255,200,61,0.08)]" : "bg-white hover:bg-[#fafbfc]",
      )}
    >
      <span
        aria-hidden="true"
        data-testid="execution-row-bar"
        className={cn(
          "absolute inset-y-0 left-0 w-[3px]",
          STATUS_BAR[row.status],
        )}
      />
      {/* O botão da linha: o clique dele sobe para o <li>, que seleciona. */}
      <button
        type="button"
        aria-current={selected || undefined}
        className={cn(
          "flex min-w-0 flex-1 items-center gap-3 text-left",
          FOCUS_RING_CLASS,
        )}
      >
        {row.originalCode ? (
          <span className="flex shrink-0 items-center gap-1">
            <span
              data-testid="execution-row-original-id"
              title={t("Original ID")}
              className="min-w-[48px] rounded bg-[#f1f3f6] px-2 py-0.5 text-center font-mono text-xs font-medium text-[#596b85]"
            >
              <span className="sr-only">{t("Original ID")}: </span>
              {row.originalCode}
            </span>
            <span
              data-testid="execution-row-edited-id"
              title={t("Edited ID")}
              className="min-w-[48px] rounded bg-[#ffedd5] px-2 py-0.5 text-center font-mono text-xs font-semibold text-[#9a3412]"
            >
              <span className="sr-only">{t("Edited ID")}: </span>
              {row.code}
            </span>
          </span>
        ) : (
          <span className="min-w-[48px] shrink-0 rounded bg-[#f1f3f6] px-2 py-0.5 text-center font-mono text-xs font-medium text-[#08254c]">
            {row.code}
          </span>
        )}
        <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2.5">
          <span className="min-w-0 break-words text-sm font-semibold text-[#08254c]">
            {row.name}
          </span>
          {row.typeLabel && (
            <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[#596b85]">
              {row.typeLabel}
            </span>
          )}
        </span>
      </button>
      <span
        data-testid="execution-row-status"
        className={cn(
          "flex shrink-0 items-center gap-1.5 text-xs font-medium",
          STATUS_TEXT[row.status],
        )}
      >
        <span
          aria-hidden="true"
          className={cn("h-1.5 w-1.5 rounded-full", STATUS_DOT[row.status])}
        />
        {row.statusLabel ?? t(STATUS_LABEL[row.status])}
      </span>
      <div className="flex shrink-0 items-center gap-1">
        {row.run ? (
          <button
            type="button"
            aria-label={row.run.label}
            title={row.run.title}
            disabled={row.run.disabled || row.run.busy}
            aria-busy={row.run.busy}
            data-testid={row.run.testId}
            onClick={(event) => {
              stop(event);
              if (!row.run || row.run.disabled || row.run.busy) return;
              row.run.onRun();
            }}
            className={RUN_ACTION_CLASS}
          >
            {row.run.busy ? (
              <Loader2
                aria-hidden="true"
                className="animate-spin motion-reduce:animate-none"
              />
            ) : (
              <Play aria-hidden="true" fill="currentColor" />
            )}
          </button>
        ) : (
          // Sem ▶ (concluída ou aguardando a pessoa): a coluna das ações não anda.
          <span aria-hidden="true" className="h-8 w-8 shrink-0" />
        )}
        {row.details && (
          <button
            type="button"
            aria-label={row.details.label}
            title={row.details.disabledReason ?? t("View details")}
            disabled={!!row.details.disabledReason}
            onClick={(event) => {
              stop(event);
              if (row.details?.disabledReason) return;
              row.details?.onDetails();
            }}
            className={GHOST_ACTION_CLASS}
          >
            <PanelRightOpen aria-hidden="true" />
          </button>
        )}
        {row.launch &&
          (row.launch.url ? (
            <a
              href={row.launch.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={row.launch.label}
              title={t("Open in SAP")}
              onClick={stop}
              className={GHOST_ACTION_CLASS}
            >
              <ExternalLink aria-hidden="true" />
            </a>
          ) : (
            <button
              type="button"
              disabled
              aria-label={row.launch.label}
              title={row.launch.disabledReason ?? t("Open in SAP")}
              className={GHOST_ACTION_CLASS}
            >
              <ExternalLink aria-hidden="true" />
            </button>
          ))}
      </div>
    </li>
  );
}

/** O "Run all" do cartão: navy quando pode, cinza com o motivo quando não. */
export function RunAllButton({
  label,
  disabledReason,
  onClick,
}: {
  label: string;
  /** Por que não pode começar, já traduzido; `null` libera. */
  disabledReason: string | null;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabledReason !== null}
      title={disabledReason || undefined}
      onClick={onClick}
      className={cn(
        "inline-flex h-9 items-center gap-2 rounded-md bg-[#08254c] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#08254c]/90",
        "disabled:cursor-not-allowed disabled:bg-[#e8ebf0] disabled:text-[#8a97ab]",
        FOCUS_RING_CLASS,
      )}
    >
      <Play aria-hidden="true" fill="currentColor" className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}
